import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import type { LucideIcon } from 'lucide-react';
import {
  AlertCircle,
  ArrowLeft,
  Brain,
  Check,
  CreditCard,
  Eye,
  Image,
  ListOrdered,
  Loader2,
  Trash2,
} from 'lucide-react';
import { useTopic } from '../hooks/useTopics';
import { useWords, type Word } from '../hooks/useWords';
import { useQuiz, useQuizzes, type Quiz } from '../hooks/useQuizzes';

type QuizType = Quiz['quizType'];

/** PATCH payload accepted by the server. `settings`/`wordIds` are not on `Quiz`,
 *  so the extra members are declared here instead of casting. */
type QuizPatch = Partial<Quiz> & {
  settings?: Record<string, unknown>;
  wordIds?: number[];
};

const quizTypes: { id: QuizType; name: string; desc: string; icon: LucideIcon }[] = [
  { id: 'memory-game', name: 'Memory Game', desc: 'Найди пары: турецкое слово и русский перевод', icon: Brain },
  { id: 'flashcards', name: 'Flashcards', desc: 'Учите слова с переворотными карточками', icon: CreditCard },
  { id: 'image-pairing', name: 'Image Pairing', desc: 'Соединяй картинки со значениями', icon: Image },
  { id: 'image-sequencing', name: 'Image Sequencing', desc: 'Расставь картинки в правильном порядке', icon: ListOrdered },
];

/**
 * Minimum playable sizes. The two pairing games are only a game with enough pairs
 * (6 pairs = 12 cards), sequencing needs a sequence to reorder, flashcards work
 * with a single card.
 */
const MIN_WORDS: Record<QuizType, number> = {
  'memory-game': 12,
  'image-pairing': 12,
  'image-sequencing': 3,
  flashcards: 1,
};

const MIN_WORDS_HINT: Record<QuizType, string> = {
  'memory-game': 'Нужно минимум 12 слов (6 пар).',
  'image-pairing': 'Нужно минимум 12 слов (6 пар).',
  'image-sequencing': 'Нужно минимум 3 слова, чтобы было что упорядочивать.',
  flashcards: 'Нужно хотя бы одно слово.',
};

interface BuilderSettings {
  /** memory-game */
  gridLayout: boolean;
  memoryAllowRetry: boolean;
  /** flashcards */
  labels: { next: string; previous: string; check: string; showSolution: string; results: string };
  /** image-pairing */
  shuffle: boolean;
  pairingAllowRetry: boolean;
  /** image-sequencing */
  showDescriptions: boolean;
  activityType: 'drag-drop' | 'click-order';
}

const DEFAULT_SETTINGS: BuilderSettings = {
  gridLayout: true,
  memoryAllowRetry: true,
  labels: {
    next: 'Далее',
    previous: 'Назад',
    check: 'Проверить',
    showSolution: 'Показать ответ',
    results: 'Результат',
  },
  shuffle: true,
  pairingAllowRetry: true,
  showDescriptions: true,
  activityType: 'drag-drop',
};

const STEPS = ['Тип', 'Слова', 'Настройки', 'Просмотр'];

function errorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError<{ error?: string }>(err) && err.response?.data?.error) {
    return err.response.data.error;
  }
  return fallback;
}

function parseSettingsJson(raw: string | null | undefined): Record<string, unknown> {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function readWordIds(raw: Record<string, unknown>): number[] {
  const ids: unknown[] = Array.isArray(raw.wordIds) ? (raw.wordIds as unknown[]) : [];
  return ids.filter((id): id is number => typeof id === 'number');
}

function readSettings(raw: Record<string, unknown>): BuilderSettings {
  const next: BuilderSettings = { ...DEFAULT_SETTINGS, labels: { ...DEFAULT_SETTINGS.labels } };
  if (typeof raw.gridLayout === 'boolean') next.gridLayout = raw.gridLayout;
  if (typeof raw.allowRetry === 'boolean') {
    next.memoryAllowRetry = raw.allowRetry;
    next.pairingAllowRetry = raw.allowRetry;
  }
  if (typeof raw.shuffle === 'boolean') next.shuffle = raw.shuffle;
  if (typeof raw.showDescriptions === 'boolean') next.showDescriptions = raw.showDescriptions;
  if (raw.activityType === 'drag-drop' || raw.activityType === 'click-order') {
    next.activityType = raw.activityType;
  }
  if (raw.labels && typeof raw.labels === 'object') {
    const labels = raw.labels as Record<string, unknown>;
    (Object.keys(next.labels) as (keyof BuilderSettings['labels'])[]).forEach((key) => {
      const value = labels[key];
      if (typeof value === 'string' && value.trim()) next.labels[key] = value;
    });
  }
  return next;
}

/** Only the keys that belong to the chosen activity are persisted. */
function buildSettings(type: QuizType, settings: BuilderSettings): Record<string, unknown> {
  switch (type) {
    case 'memory-game':
      return { gridLayout: settings.gridLayout, allowRetry: settings.memoryAllowRetry };
    case 'image-pairing':
      return { shuffle: settings.shuffle, allowRetry: settings.pairingAllowRetry };
    case 'image-sequencing':
      return { showDescriptions: settings.showDescriptions, activityType: settings.activityType };
    case 'flashcards':
      return { labels: { ...settings.labels } };
  }
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white p-3 hover:bg-gray-50">
      <span className="min-w-0">
        <span className="block text-sm font-medium text-gray-900">{label}</span>
        {hint && <span className="block text-xs text-gray-500">{hint}</span>}
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      <span
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
          checked ? 'bg-brand-600' : 'bg-gray-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </span>
    </label>
  );
}

function LabelField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
      />
    </div>
  );
}

/**
 * Static, self-contained mock of the student view. The player page fetches a
 * published quiz and is owned elsewhere, so the builder renders its own sketch of
 * what the chosen type + selection will look like.
 */
function QuizPreview({
  type,
  words,
  settings,
}: {
  type: QuizType;
  words: Word[];
  settings: BuilderSettings;
}) {
  if (words.length === 0) {
    return <p className="text-sm text-gray-500">Слова не выбраны — предпросмотр пустой.</p>;
  }

  if (type === 'memory-game') {
    const cards = words.slice(0, 12);
    return (
      <div className="space-y-3">
        <div className="flex justify-between text-xs text-gray-500">
          <span>Карточек: {words.length * 2} (пар: {words.length})</span>
          <span>{settings.memoryAllowRetry ? 'Повтор разрешён' : 'Без повтора'}</span>
        </div>
        <div className={`grid gap-2 ${settings.gridLayout ? 'grid-cols-3 sm:grid-cols-4' : 'grid-cols-2'}`}>
          {cards.map((word) => (
            <div
              key={word.id}
              className="flex h-16 items-center justify-center rounded-xl bg-white text-lg font-semibold text-gray-400 shadow-sm"
            >
              ?
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400">
          {settings.gridLayout
            ? 'Компактная сетка 4×3 — карточек больше на экране.'
            : 'Крупная сетка 2×N — карточки легче нажимать на телефоне.'}
        </p>
      </div>
    );
  }

  if (type === 'flashcards') {
    const first = words[0];
    return (
      <div className="mx-auto max-w-sm space-y-3">
        <p className="text-center text-xs text-gray-500">
          Карточка 1 из {words.length}
        </p>
        <div className="flex h-48 flex-col items-center justify-center gap-2 rounded-2xl bg-white p-6 text-center shadow-sm">
          <span className="text-2xl font-bold text-gray-900">{first.turkish}</span>
          <span className="text-sm text-gray-400">После переворота: {first.russian || '—'}</span>
        </div>
        <div className="flex items-center justify-center gap-2">
          <span className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-sm">
            {settings.labels.previous}
          </span>
          <span className="rounded-lg bg-brand-600 px-3 py-2 text-xs font-medium text-white shadow-sm">
            {settings.labels.next}
          </span>
        </div>
        <div className="flex items-center justify-center gap-2">
          <span className="rounded-lg bg-green-600 px-3 py-2 text-xs font-medium text-white shadow-sm">
            {settings.labels.check}
          </span>
          <span className="rounded-lg bg-gray-600 px-3 py-2 text-xs font-medium text-white shadow-sm">
            {settings.labels.showSolution}
          </span>
          <span className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-sm">
            {settings.labels.results}
          </span>
        </div>
      </div>
    );
  }

  if (type === 'image-pairing') {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            {words.slice(0, 4).map((word) => (
              <div key={word.id} className="rounded-lg bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm">
                {word.turkish}
              </div>
            ))}
          </div>
          <div className="space-y-2">
            {(settings.shuffle ? [...words].slice(4, 8).reverse() : words.slice(4, 8)).map((word) => (
              <div
                key={word.id}
                className="flex h-12 items-center justify-center rounded-lg bg-brand-50 text-xs text-brand-600"
              >
                {word.imagePath ? 'Картинка' : 'Нет картинки'}
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-gray-400">
          {settings.shuffle
            ? 'Картинки перемешиваются при запуске — порядок не подсказывает ответ.'
            : 'Картинки идут в порядке слов — проще, но ответ виден сразу.'}
          {settings.pairingAllowRetry ? ' Повтор разрешён.' : ' Без повтора.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <ol className="space-y-2">
        {words.slice(0, 5).map((word, index) => (
          <li
            key={word.id}
            className="flex items-center gap-3 rounded-lg bg-white p-2 shadow-sm"
          >
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700">
              {index + 1}
            </span>
            <span className="flex h-9 w-9 items-center justify-center rounded bg-brand-50 text-[10px] text-brand-600">
              {word.imagePath ? 'Картинка' : '—'}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium text-gray-900">{word.turkish}</span>
              {settings.showDescriptions && (
                <span className="block truncate text-xs text-gray-500">{word.russian || '—'}</span>
              )}
            </span>
          </li>
        ))}
      </ol>
      <p className="text-xs text-gray-400">
        {settings.activityType === 'drag-drop'
          ? 'Ученик перетаскивает картинки в нужный порядок.'
          : 'Ученик выбирает картинки по очереди, одним касанием.'}
        {settings.showDescriptions ? ' Подписи видны во время игры.' : ' Подписи скрыты.'}
      </p>
    </div>
  );
}

export default function QuizBuilderPage() {
  const { topicId: topicIdParam, id: quizIdParam } = useParams<{ topicId: string; id: string }>();
  const navigate = useNavigate();

  const editId = quizIdParam ? Number(quizIdParam) : undefined;
  const { quiz, loading: quizLoading, fetchQuiz } = useQuiz(editId);
  const topicId = editId ? quiz?.topicId : Number(topicIdParam);

  // `useQuiz(undefined)` keeps `loading === true` forever (its fetch early-returns),
  // so the spinner must only wait for the quiz on the edit route.
  const loadingQuiz = editId !== undefined && quizLoading;
  const quizMissing = editId !== undefined && !quizLoading && !quiz;

  const { topic, loading: topicLoading } = useTopic(topicId);
  const { words, loading: wordsLoading } = useWords(topicId);
  const { createQuiz, updateQuiz, deleteQuiz } = useQuizzes();

  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState<QuizType | ''>('');
  const [selectedWords, setSelectedWords] = useState<Set<number>>(new Set());
  const [title, setTitle] = useState('');
  const [settings, setSettings] = useState<BuilderSettings>(DEFAULT_SETTINGS);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const hydrated = useRef(false);

  // Load an existing quiz once: prefill the wizard so /quizzes/:id/edit works.
  useEffect(() => {
    if (!quiz || hydrated.current) return;
    hydrated.current = true;
    const stored = parseSettingsJson(quiz.settingsJson);
    setSelectedType(quiz.quizType);
    setTitle(quiz.title);
    setSettings(readSettings(stored));
    setSelectedWords(new Set(readWordIds(stored)));
  }, [quiz]);

  const selectedList = useMemo(
    () => words.filter((word) => selectedWords.has(word.id)),
    [words, selectedWords]
  );

  // A saved selection can outlive the words it points at (deleted from the topic).
  // The player only receives words that still exist, so validate against that.
  const missingWords = useMemo(() => {
    if (words.length === 0) return 0;
    const known = new Set(words.map((word) => word.id));
    return Array.from(selectedWords).filter((id) => !known.has(id)).length;
  }, [words, selectedWords]);

  const effectiveWordCount = selectedWords.size - missingWords;
  const minWords = selectedType ? MIN_WORDS[selectedType] : 0;
  const notEnoughWords = selectedType !== '' && effectiveWordCount < minWords;
  const selectedTypeMeta = quizTypes.find((t) => t.id === selectedType);

  const toggleWord = (id: number) => {
    const next = new Set(selectedWords);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedWords(next);
    setError('');
  };

  const goNext = () => {
    setError('');
    if (notEnoughWords && selectedType) {
      setError(`${MIN_WORDS_HINT[selectedType]} Доступно для теста: ${effectiveWordCount}.`);
      return;
    }
    setStep((s) => Math.min(STEPS.length, s + 1));
  };

  const handleSubmit = async () => {
    if (!selectedType || !title.trim() || !topic) {
      setError('Укажите название теста.');
      return;
    }
    if (notEnoughWords) {
      setError(`${MIN_WORDS_HINT[selectedType]} Доступно для теста: ${effectiveWordCount}.`);
      return;
    }
    const wordIds = Array.from(selectedWords);
    const payload: QuizPatch = {
      title: title.trim(),
      quizType: selectedType,
      settings: buildSettings(selectedType, settings),
      wordIds,
    };

    setIsSubmitting(true);
    setError('');
    try {
      if (editId && quiz) {
        await updateQuiz(quiz.id, payload);
        await fetchQuiz();
        navigate(`/quizzes/${quiz.id}/publish`);
      } else {
        const created = await createQuiz({
          topicId: topic.id,
          title: title.trim(),
          quizType: selectedType,
          wordIds,
          settings: buildSettings(selectedType, settings),
        });
        navigate(`/quizzes/${created.id}/publish`);
      }
    } catch (err: unknown) {
      setError(errorMessage(err, 'Не удалось сохранить тест'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!quiz) return;
    setIsDeleting(true);
    setError('');
    try {
      await deleteQuiz(quiz.id);
      navigate(`/topics/${quiz.topicId}`);
    } catch (err: unknown) {
      setIsDeleting(false);
      setConfirmDelete(false);
      setError(errorMessage(err, 'Не удалось удалить тест'));
    }
  };

  if (quizMissing) {
    return (
      <div className="flex h-64 flex-col items-center justify-center">
        <p className="text-gray-500">Тест не найден</p>
        <button onClick={() => navigate('/topics')} className="mt-2 text-sm text-brand-600 hover:underline">
          ← К списку тем
        </button>
      </div>
    );
  }

  if (loadingQuiz || topicLoading || wordsLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  if (!topic) {
    return (
      <div className="flex h-64 flex-col items-center justify-center">
        <p className="text-gray-500">Тема не найдена</p>
        <button onClick={() => navigate('/topics')} className="mt-2 text-sm text-brand-600 hover:underline">
          ← К списку тем
        </button>
      </div>
    );
  }

  const backTo = quiz ? `/topics/${quiz.topicId}` : `/topics/${topicIdParam}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(backTo)}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">
            {quiz ? 'Редактирование теста' : 'Создать тест'}
          </h1>
          <p className="text-sm text-gray-500">Тема: {topic.title}</p>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 text-sm">
        {STEPS.map((s, i) => (
          <div
            key={s}
            className={`flex items-center gap-2 ${i + 1 === step ? 'font-medium text-brand-600' : 'text-gray-400'}`}
          >
            <span
              className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${i + 1 === step ? 'bg-brand-100' : 'bg-gray-100'}`}
            >
              {i + 1}
            </span>
            {s}
            {i < STEPS.length - 1 && <span className="mx-1 text-gray-300">→</span>}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="grid gap-4 sm:grid-cols-2">
          {quizTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => {
                setSelectedType(type.id);
                setError('');
                setStep(2);
              }}
              className={`flex flex-col items-start rounded-xl border p-5 text-left transition-all hover:shadow-md ${
                selectedType === type.id ? 'border-brand-500 bg-brand-50' : 'border-gray-200 bg-white'
              }`}
            >
              <div
                className={`mb-3 flex h-10 w-10 items-center justify-center rounded-lg ${
                  selectedType === type.id ? 'bg-brand-100 text-brand-600' : 'bg-gray-100 text-gray-500'
                }`}
              >
                <type.icon size={20} />
              </div>
              <h3 className="font-semibold text-gray-900">{type.name}</h3>
              <p className="mt-1 text-sm text-gray-500">{type.desc}</p>
              {quiz?.isPublished && type.id !== quiz.quizType && (
                <p className="mt-2 text-xs text-amber-700">
                  Уже опубликован: смена типа изменит то, что увидят ученики по текущей ссылке.
                </p>
              )}
            </button>
          ))}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Выберите слова</h2>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedWords(new Set(words.map((w) => w.id)))}
                className="text-sm text-brand-600 hover:underline"
              >
                Выбрать все
              </button>
              <button
                onClick={() => setSelectedWords(new Set())}
                className="text-sm text-gray-500 hover:underline"
              >
                Снять все
              </button>
            </div>
          </div>

          {words.length === 0 ? (
            <p className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
              В теме пока нет слов. Добавьте слова на странице темы.
            </p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {words.map((word) => (
                <label
                  key={word.id}
                  className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors ${
                    selectedWords.has(word.id)
                      ? 'border-brand-500 bg-brand-50'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedWords.has(word.id)}
                    onChange={() => toggleWord(word.id)}
                    className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900">{word.turkish}</div>
                    <div className="truncate text-sm text-gray-500">{word.russian || '—'}</div>
                  </div>
                </label>
              ))}
            </div>
          )}

          {missingWords > 0 && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              {missingWords} из выбранных слов больше нет в теме — они не попадут в тест.
            </p>
          )}

          {selectedType && notEnoughWords && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              {MIN_WORDS_HINT[selectedType]} Доступно для теста: {effectiveWordCount}.
            </p>
          )}

          <div className="flex justify-end gap-3">
            <button
              onClick={() => setStep(1)}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Назад
            </button>
            <button
              onClick={goNext}
              disabled={notEnoughWords}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              Далее
            </button>
          </div>
        </div>
      )}

      {step === 3 && selectedType && (
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Название теста</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={`${topic.title} — ${selectedTypeMeta?.name}`}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>

          <div className="space-y-2">
            <h2 className="text-lg font-semibold text-gray-900">Настройки: {selectedTypeMeta?.name}</h2>

            {selectedType === 'memory-game' && (
              <div className="space-y-2">
                <Toggle
                  label="Сетка карточек 4×3"
                  hint="Больше карточек на экране, мельче нажатия"
                  checked={settings.gridLayout}
                  onChange={(value) => setSettings({ ...settings, gridLayout: value })}
                />
                <Toggle
                  label="Разрешить повтор"
                  hint="Ученик сможет пройти тест заново"
                  checked={settings.memoryAllowRetry}
                  onChange={(value) => setSettings({ ...settings, memoryAllowRetry: value })}
                />
              </div>
            )}

            {selectedType === 'image-pairing' && (
              <div className="space-y-2">
                <Toggle
                  label="Перемешать картинки"
                  hint="Порядок не подсказывает ответ"
                  checked={settings.shuffle}
                  onChange={(value) => setSettings({ ...settings, shuffle: value })}
                />
                <Toggle
                  label="Разрешить повтор"
                  hint="Ученик сможет пройти тест заново"
                  checked={settings.pairingAllowRetry}
                  onChange={(value) => setSettings({ ...settings, pairingAllowRetry: value })}
                />
              </div>
            )}

            {selectedType === 'image-sequencing' && (
              <div className="space-y-2">
                <Toggle
                  label="Показывать подписи"
                  hint="Русский перевод виден во время игры"
                  checked={settings.showDescriptions}
                  onChange={(value) => setSettings({ ...settings, showDescriptions: value })}
                />
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Тип задания</label>
                  <div className="flex flex-wrap gap-2">
                    {(
                      [
                        { id: 'drag-drop', label: 'Перетаскивание' },
                        { id: 'click-order', label: 'Выбор по очереди' },
                      ] as const
                    ).map((option) => (
                      <button
                        key={option.id}
                        onClick={() => setSettings({ ...settings, activityType: option.id })}
                        className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                          settings.activityType === option.id
                            ? 'border-brand-500 bg-brand-50 text-brand-700'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {selectedType === 'flashcards' && (
              <div className="grid gap-3 sm:grid-cols-2">
                <LabelField
                  label="Кнопка «Далее»"
                  value={settings.labels.next}
                  onChange={(value) => setSettings({ ...settings, labels: { ...settings.labels, next: value } })}
                />
                <LabelField
                  label="Кнопка «Назад»"
                  value={settings.labels.previous}
                  onChange={(value) => setSettings({ ...settings, labels: { ...settings.labels, previous: value } })}
                />
                <LabelField
                  label="Кнопка «Проверить»"
                  value={settings.labels.check}
                  onChange={(value) => setSettings({ ...settings, labels: { ...settings.labels, check: value } })}
                />
                <LabelField
                  label="Кнопка «Показать ответ»"
                  value={settings.labels.showSolution}
                  onChange={(value) =>
                    setSettings({ ...settings, labels: { ...settings.labels, showSolution: value } })
                  }
                />
                <LabelField
                  label="Заголовок результата"
                  value={settings.labels.results}
                  onChange={(value) => setSettings({ ...settings, labels: { ...settings.labels, results: value } })}
                />
              </div>
            )}
          </div>

          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <h3 className="text-sm font-medium text-gray-700">
              Выбрано слов: {selectedWords.size}
              {missingWords > 0 && ` (доступно: ${effectiveWordCount})`}
              {selectedType && ` · минимум ${minWords}`}
            </h3>
            <p className="text-sm text-gray-500">Тип: {selectedTypeMeta?.name}</p>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => setStep(2)}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Назад
            </button>
            <button
              onClick={goNext}
              disabled={!title.trim()}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              Далее
            </button>
          </div>
        </div>
      )}

      {step === 4 && selectedType && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Eye size={18} className="text-brand-600" />
            <h2 className="text-lg font-semibold text-gray-900">Предпросмотр</h2>
          </div>
          <p className="text-sm text-gray-500">
            «{title.trim() || 'Без названия'}» · {selectedTypeMeta?.name} · {selectedWords.size}{' '}
            {selectedWords.size === 1 ? 'слово' : 'слов'}. Это приблизительный вид: у ученика тест
            отрисуется с его картинками и переводами.
          </p>

          <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 shadow-sm">
            <QuizPreview type={selectedType} words={selectedList} settings={settings} />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              {quiz && (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="flex items-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                >
                  <Trash2 size={16} />
                  Удалить тест
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setStep(3)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Назад
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !title.trim()}
                className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                {quiz ? 'Сохранить' : 'Создать тест'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && quiz && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Удалить тест?</h3>
            <p className="mt-2 text-sm text-gray-500">
              «{quiz.title}» будет удалён вместе с его настройками. Слова темы останутся, а ссылка и
              QR-код перестанут работать. Действие необратимо.
            </p>
            {quiz.isPublished && (
              <p className="mt-2 text-sm font-medium text-amber-700">
                Тест опубликован: ученики, уже открывшие ссылку, получат ошибку.
              </p>
            )}
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setConfirmDelete(false)}
                disabled={isDeleting}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting && <Loader2 size={16} className="animate-spin" />}
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}